import type {
  IssuePriority,
  IssueType,
  ProjectMemberRole,
  StatusCategory,
} from "../../../core/data/redux/projects/types";

export const ISSUE_TYPES: { value: IssueType; label: string; color: string }[] = [
  { value: "TASK", label: "Task", color: "#3B82F6" },
  { value: "BUG", label: "Bug", color: "#EF4444" },
  { value: "STORY", label: "Story", color: "#10B981" },
  { value: "EPIC", label: "Epic", color: "#8B5CF6" },
  { value: "SUBTASK", label: "Subtask", color: "#6B7280" },
];

export const PRIORITY_OPTIONS: {
  value: IssuePriority;
  label: string;
  color: string;
}[] = [
  { value: "LOWEST", label: "Lowest", color: "#94A3B8" },
  { value: "LOW", label: "Low", color: "#60A5FA" },
  { value: "MEDIUM", label: "Medium", color: "#F59E0B" },
  { value: "HIGH", label: "High", color: "#F97316" },
  { value: "HIGHEST", label: "Highest", color: "#DC2626" },
];

export const STATUS_CATEGORIES: {
  value: StatusCategory;
  label: string;
  color: string;
}[] = [
  { value: "TODO", label: "To Do", color: "#6B7280" },
  { value: "IN_PROGRESS", label: "In Progress", color: "#3B82F6" },
  { value: "DONE", label: "Done", color: "#10B981" },
];

export const STATUS_CATEGORY_COLORS: Record<StatusCategory, string> = {
  TODO: "#6B7280",
  IN_PROGRESS: "#3B82F6",
  DONE: "#10B981",
};

export const PROJECT_MEMBER_ROLES: {
  value: ProjectMemberRole;
  label: string;
  description: string;
}[] = [
  { value: "ADMIN", label: "Admin", description: "Full project control" },
  {
    value: "MANAGER",
    label: "Manager",
    description: "Manage members, sprints, board",
  },
  { value: "LEAD", label: "Lead", description: "Lead a team within the project" },
  { value: "MEMBER", label: "Member", description: "Work on issues" },
  { value: "VIEWER", label: "Viewer", description: "Read-only access" },
];

export const PROJECT_PRIORITY_OPTIONS = [
  { value: 0, label: "Low" },
  { value: 1, label: "Medium" },
  { value: 2, label: "High" },
];

export const PROJECT_VISIBILITY_OPTIONS = [
  { value: 0, label: "Public" },
  { value: 1, label: "Private" },
  { value: 2, label: "Admin Only" },
];

export const PROJECT_STATUS_OPTIONS = [
  { value: 1, label: "Active" },
  { value: 0, label: "Inactive" },
];

export const DEFAULT_BOARD_COLUMN_COLORS = [
  "#6B7280",
  "#3B82F6",
  "#F59E0B",
  "#8B5CF6",
  "#EC4899",
  "#10B981",
];

export const PROJECT_CAPABILITIES = {
  VIEW: ["ADMIN", "MANAGER", "LEAD", "MEMBER", "VIEWER"],
  CREATE_ISSUE: ["ADMIN", "MANAGER", "LEAD", "MEMBER"],
  DELETE_ISSUE: ["ADMIN", "MANAGER", "LEAD"],
  MANAGE_SPRINT: ["ADMIN", "MANAGER", "LEAD"],
  DELETE_SPRINT: ["ADMIN", "MANAGER"],
  MANAGE_MEMBERS: ["ADMIN", "MANAGER", "LEAD"],
  CHANGE_ROLE: ["ADMIN", "MANAGER"],
  UPDATE_PROJECT: ["ADMIN", "MANAGER"],
  DELETE_PROJECT: ["ADMIN"],
  MANAGE_LABEL: ["ADMIN", "MANAGER", "LEAD"],
  MANAGE_BASELINE: ["ADMIN", "MANAGER"],
  MANAGE_DEPENDENCY: ["ADMIN", "MANAGER", "LEAD"],
  MANAGE_SCHEDULE: ["ADMIN", "MANAGER", "LEAD"],
  MANAGE_COMPONENT: ["ADMIN", "MANAGER"],
  MANAGE_VERSION: ["ADMIN", "MANAGER"],
  MANAGE_CUSTOM_FIELD: ["ADMIN", "MANAGER"],
  MANAGE_RESOLUTION: ["ADMIN", "MANAGER"],
  MANAGE_ATTACHMENT: ["ADMIN", "MANAGER", "LEAD", "MEMBER"],
  MANAGE_WATCHER: ["ADMIN", "MANAGER", "LEAD", "MEMBER"],
  MANAGE_WORKLOG: ["ADMIN", "MANAGER", "LEAD", "MEMBER"],
  MANAGE_LINK: ["ADMIN", "MANAGER", "LEAD", "MEMBER"],
} as const;

export type ProjectCapability = keyof typeof PROJECT_CAPABILITIES;
